import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shirt, Briefcase, CalendarCheck, Gift, IdCard, FileImage,
  ArrowRight, MessageCircle, PenTool, Palette, Printer, Truck,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import QuoteDialog from "@/components/QuoteDialog";
import { SectionTitle } from "@/components/SectionTitle";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";
import { api, fileUrl } from "@/lib/api";
import { buildWhatsAppLink, categoryQuoteMessage } from "@/lib/whatsapp";
import { toast } from "sonner";

const CATEGORIES = [
  { icon: Shirt, label: "Camisetas", desc: "Malhas personalizadas para equipes, campanhas e eventos." },
  { icon: Briefcase, label: "Uniformes empresariais", desc: "Identidade profissional do escritório ao operacional." },
  { icon: CalendarCheck, label: "Eventos e staff", desc: "Kits de equipe, camisetas de staff e organização." },
  { icon: Gift, label: "Brindes", desc: "Peças e itens promocionais com a sua marca." },
  { icon: IdCard, label: "Credenciais / crachás", desc: "Credenciais, cordões e crachás para eventos." },
  { icon: FileImage, label: "Materiais gráficos", desc: "Impressos e comunicação visual sob medida." },
];

const STEPS = [
  { icon: MessageCircle, t: "Solicite o orçamento", d: "Conte o que precisa pelo formulário ou WhatsApp." },
  { icon: PenTool, t: "Aprovação da arte", d: "Enviamos a proposta e alinhamos o layout com você." },
  { icon: Printer, t: "Produção", d: "Personalizamos suas peças com acabamento de qualidade." },
  { icon: Truck, t: "Entrega / retirada", d: "Combinamos a forma de entrega mais prática pra você." },
];

const WHY = [
  { t: "Atendimento próximo", d: "Você fala direto com a equipe que produz." },
  { t: "Personalização real", d: "Cada projeto é tratado de forma exclusiva." },
  { t: "Acabamento cuidadoso", d: "Atenção aos detalhes em cada peça." },
];

const FAQ = [
  { q: "Vocês atendem pedidos de pequena quantidade?", a: "Sim. Envie seu pedido pelo formulário de orçamento e retornamos com as condições para a sua quantidade." },
  { q: "Como funciona a personalização?", a: "Alinhamos o layout e a arte com você antes de produzir. Nada é impresso sem a sua aprovação." },
  { q: "Vocês criam a arte?", a: "Podemos ajustar e finalizar a arte junto com você. Fale com a equipe no orçamento." },
];

export default function StampPage() {
  const { settings } = useSite();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [preset, setPreset] = useState("");
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [filter, setFilter] = useState("Todos");

  useEffect(() => {
    document.title = "K8 Stamp | Vista sua equipe. Destaque sua marca.";
    api.get("/gallery?brand=stamp").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/testimonials?brand=stamp").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const openQuote = (product = "") => {
    setPreset(product);
    setQuoteOpen(true);
  };

  const categoryWhats = (label) => {
    if (!settings?.whatsapp_number) {
      openQuote(label);
      return;
    }
    window.open(
      buildWhatsAppLink(settings.whatsapp_number, categoryQuoteMessage("K8 Stamp", label)),
      "_blank"
    );
  };

  const cats = ["Todos", ...new Set(gallery.map((g) => g.category).filter(Boolean))];
  const filtered = filter === "Todos" ? gallery : gallery.filter((g) => g.category === filter);

  return (
    <div className="App">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[var(--accent)]">
              Personalização & Impressão
            </span>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mt-4">
              Vista sua equipe.
              <br />
              <span className="brand-gradient-text">Destaque sua marca.</span>
            </h1>
            <p className="text-[var(--text-muted)] text-lg mt-6 max-w-lg leading-relaxed">
              Uniformes, camisetas personalizadas, brindes e materiais gráficos de alta precisão para empresas, eventos e equipes.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button data-testid="hero-quote-button" onClick={() => openQuote()} className="btn-primary font-bold h-12 px-7 text-base">
                Solicitar orçamento <ArrowRight size={18} className="ml-2" />
              </Button>
              <a href="#produtos">
                <Button data-testid="hero-products-button" variant="outline" className="h-12 px-7 text-base border-[var(--border)] bg-transparent hover:bg-[var(--bg-card)]">
                  Conhecer produtos
                </Button>
              </a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
            <div className="absolute -inset-4 rounded-3xl opacity-30 blur-2xl accent-bar" />
            <img
              src={fileUrl(settings?.stamp_hero_image)}
              alt="Peças personalizadas K8 Stamp"
              className="relative rounded-2xl w-full h-[420px] object-cover border border-[var(--border)]"
            />
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="produtos" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionTitle eyebrow="O que fazemos" title="Categorias de produtos"
          subtitle="Escolha uma categoria e solicite um orçamento contextualizado direto com a nossa equipe." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((c, i) => (
            <motion.div key={c.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="card-surface p-7 flex flex-col" data-testid={`category-card-${i}`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 accent-bar text-black">
                <c.icon size={22} />
              </div>
              <h3 className="font-head font-bold text-xl">{c.label}</h3>
              <p className="text-[var(--text-muted)] text-sm mt-2 flex-1">{c.desc}</p>
              <button
                onClick={() => categoryWhats(c.label)}
                data-testid={`category-quote-${i}`}
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--accent)] hover:gap-3 transition-all"
              >
                Solicitar orçamento <ArrowRight size={16} />
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section id="galeria" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionTitle eyebrow="Portfólio" title="Galeria de trabalhos"
          subtitle="Trabalhos reais da K8 Stamp. As imagens marcadas como exemplo serão substituídas por fotos reais." />
        {gallery.length === 0 ? (
          <div className="card-surface p-10 text-center text-[var(--text-muted)]" data-testid="gallery-empty">
            A galeria será exibida assim que os trabalhos forem cadastrados no painel.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-8">
              {cats.map((c) => (
                <button key={c} onClick={() => setFilter(c)} data-testid={`gallery-filter-${c}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                    filter === c ? "btn-primary" : "border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text-main)]"
                  }`}>
                  {c}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map((g) => (
                <div key={g.id} className="card-surface overflow-hidden group" data-testid={`gallery-item-${g.id}`}>
                  <div className="relative aspect-square overflow-hidden">
                    {g.image_url ? (
                      <img src={fileUrl(g.image_url)} alt={g.title || "Trabalho K8 Stamp"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[var(--bg-secondary)] text-[var(--text-muted)] text-sm">
                        Sem imagem
                      </div>
                    )}
                    {g.is_placeholder && (
                      <span className="absolute top-2 left-2 text-[10px] font-bold uppercase bg-black/70 text-white px-2 py-1 rounded">
                        Exemplo
                      </span>
                    )}
                  </div>
                  {g.title && <div className="p-3 text-sm">{g.title}</div>}
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* HOW TO ORDER */}
      <section id="como-pedir" className="bg-[var(--bg-secondary)] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle eyebrow="Passo a passo" title="Como pedir" center />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.t} className="text-center" data-testid={`step-${i}`}>
                <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center accent-bar text-black mb-4">
                  <s.icon size={24} />
                </div>
                <div className="text-xs font-bold text-[var(--accent)] mb-1">Etapa {i + 1}</div>
                <h3 className="font-head font-bold text-lg">{s.t}</h3>
                <p className="text-[var(--text-muted)] text-sm mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <SectionTitle eyebrow="Diferenciais" title="Por que escolher a K8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {WHY.map((w) => (
            <div key={w.t} className="card-surface p-7">
              <Palette className="text-[var(--accent)] mb-4" />
              <h3 className="font-head font-bold text-lg">{w.t}</h3>
              <p className="text-[var(--text-muted)] text-sm mt-2">{w.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS (hidden if empty) */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <SectionTitle eyebrow="Depoimentos" title="O que dizem sobre a K8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.id} className="card-surface p-7" data-testid={`testimonial-${t.id}`}>
                <p className="text-[var(--text-main)]">“{t.text}”</p>
                <div className="mt-4 text-sm font-semibold">{t.name}</div>
                {t.role && <div className="text-xs text-[var(--text-muted)]">{t.role}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionTitle eyebrow="Dúvidas" title="Perguntas frequentes" center />
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-[var(--border)]">
              <AccordionTrigger className="text-left font-semibold" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-[var(--text-muted)]">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA + SPORTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="card-surface p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 accent-bar" />
          <div className="relative">
            <h2 className="font-head text-3xl sm:text-4xl font-black">Pronto para começar seu projeto?</h2>
            <p className="text-[var(--text-muted)] mt-3 max-w-xl mx-auto">
              Solicite um orçamento sem compromisso. A equipe da K8 Stamp retorna com a melhor proposta para você.
            </p>
            <Button onClick={() => openQuote()} data-testid="final-quote-button" className="btn-primary font-bold h-12 px-8 mt-6">
              Solicitar orçamento
            </Button>
          </div>
        </div>

        <Link to="/k8-sports" className="mt-8 block">
          <div className="rounded-2xl p-8 border border-[var(--border)] bg-gradient-to-r from-[#15151C] to-[#0D0D11] flex items-center justify-between group">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#D4AF37" }}>Conheça também</span>
              <h3 className="font-head text-2xl font-black mt-1" style={{ color: "#F5E48B" }}>K8 Sports</h3>
              <p className="text-[var(--text-muted)] text-sm mt-1">Uniformes esportivos para times, escolas e interclasse.</p>
            </div>
            <ArrowRight className="group-hover:translate-x-1 transition-transform" style={{ color: "#D4AF37" }} size={28} />
          </div>
        </Link>
      </section>

      <Footer brand="stamp" />
      <FloatingWhatsApp brandName="K8 Stamp" />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} brand="stamp" presetProduct={preset} />
    </div>
  );
}
